import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Spinner, Alert, Card } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate, useParams } from 'react-router-dom';
import { CampaignTypeService, LanguageService } from '../../../../../Services/Axios/Api';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import LanguageSelector from '../../../../../Components/LanguageSelector/LanguageSelector';

const EditCampaignType = () => {
    const { id } = useParams();
    const [title, setTitle] = useState('');
    const [mode, setMode] = useState('Normal');
    const [isGeneralDonation, setIsGeneralDonation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    
    // Language states
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);

    useEffect(() => {
        loadLanguages();
    }, []);

    // Load data after languages are loaded and current language is set
    useEffect(() => {
        if (isLanguagesLoaded && currentLanguage) {
            fetchCampaignType();
        }
    }, [isLanguagesLoaded, currentLanguage]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('EditCampaignType: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const fetchCampaignType = async () => {
        try {
            setLoading(true);
            
            // Use the ByIdAndLanguage endpoint
            const response = await CampaignTypeService.getCampaignTypeByIdAndLanguage(id, currentLanguage);
            setTitle(response.result.name);
            setMode(response.result.mode);
            setIsGeneralDonation(response.result.isGeneralDonation);
            console.log(response.result);
        } catch (error) {
            console.error('Error fetching campaign type:', error);
            toast.error('Failed to fetch campaign type');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // fetchCampaignType will be called automatically by the useEffect when currentLanguage changes
    };

    const validateForm = () => {
        const newErrors = {};
        if (!title.trim()) {
            newErrors.title = 'Title is required';
        }
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length === 0) {
            try {
                setLoading(true);
                setErrors({});
                await CampaignTypeService.updateCampaignType(id, {
                    name: title,
                    mode: mode,
                    isGeneralDonation: isGeneralDonation,
                    language: currentLanguage
                });
                setShowSuccess(true);
                toast.success('Campaign type updated successfully');
                setTimeout(() => {
                    navigate('/bj-admin/donations/campaignstype');
                }, 2000);
            } catch (error) {
                toast.error('Failed to update campaign type');
                console.error('Error updating campaign type:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setErrors(formErrors);
        }
    };

    const navigate = useNavigate();
    const handleBack = () => {
        window.history.back();
    };

    if (!isLanguagesLoaded) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-5">
            <h2 className="mb-4">Edit Campaign Type</h2>
            
            {/* Language Management Card */}
            <Card className="mb-4 language-management-card">
                <Card.Header className="language-management-header">
                    <Row className="">
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-3">
                                <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                <div>
                                    <h6 className="mb-0">Language Management</h6>
                                    <small className="text-muted">
                                        Editing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="d-flex justify-content-end">
                            <LanguageSelector
                                currentLanguage={currentLanguage}
                                availableLanguages={availableLanguages}
                                onLanguageChange={handleLanguageChange}
                                showBadge={false}
                                showCopyFromLanguage={false}
                            />
                        </Col>
                    </Row>
                </Card.Header>
                
                {/* Language Alert */}
                {showLanguageAlert && (
                    <Card.Body className="p-3 language-alert-container">
                        <Alert
                            variant={languageAlertType} 
                            dismissible 
                            onClose={() => setShowLanguageAlert(false)}
                            className="mb-0"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faGlobe} />
                                <div>
                                    {languageAlertType === 'warning' ? (
                                        <>
                                            <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                            {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                        </>
                                    ) : (
                                        <>
                                            <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this campaign type.
                                        </>
                                    )}
                                </div>
                            </div>
                        </Alert>
                    </Card.Body>
                )}
            </Card>

            {showSuccess && <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
                Campaign type updated successfully!
            </Alert>}
            
            <Form onSubmit={handleSubmit} noValidate>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formTitle">
                            <Form.Label>Title</Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Enter campaign title" 
                                value={title} 
                                onChange={(e) => setTitle(e.target.value)} 
                                isInvalid={!!errors.title}
                                disabled={loading}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.title}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formMode">
                            <Form.Label>Mode</Form.Label>
                            <Form.Select
                                as="select" 
                                value={mode} 
                                onChange={(e) => setMode(e.target.value)}
                                disabled={loading}
                            >
                                <option value="Normal">Normal</option>
                                <option value="Progressing">Progressing</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="formIsGeneralDonation">
                            <Form.Check
                                type="checkbox"
                                label="Is General Donation?"
                                checked={isGeneralDonation}
                                onChange={(e) => setIsGeneralDonation(e.target.checked)}
                                disabled={loading}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <div className="d-flex justify-content-end">
                    <Button variant="success" className='me-2' type="submit" disabled={loading}>
                        {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Submit'}
                    </Button>
                    <Button variant="danger" onClick={handleBack}>Cancel</Button>
                </div>
            </Form>
        </Container>
    );
};

export default EditCampaignType;
